<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import type { CSSProperties } from 'vue'

import BaseCluster from '~/components/foundations/BaseCluster.vue'
import BaseIcon from '~/components/foundations/BaseIcon.vue'
import TextBody from '~/components/foundations/TextBody.vue'

export type SingleSelectBoxOption = {
  label: string
  value: number | string
  searchKey?: string
  excludeFromSearch?: boolean
}

const {
  placeholder,
  width,
  options,
  selectedOption,
  clearable = true,
  optionsHeight = '180px',
  disabled = false,
} = defineProps<{
  placeholder: string
  width: CSSProperties['width']
  optionsHeight?: CSSProperties['maxHeight']
  options: SingleSelectBoxOption[]
  selectedOption: SingleSelectBoxOption | undefined
  clearable?: boolean
  disabled?: boolean
}>()
const emit = defineEmits<{
  (e: 'update:selectedOption', value: SingleSelectBoxOption | undefined): void
}>()

const showOptionList = ref(false)
const searchKeyword = ref('')
const isSearchMode = ref(false)

const searchFilteredOptions = computed(() => {
  // 検索モードでない場合、または検索キーワードが空の場合は全てのオプション
  if (!isSearchMode.value || !searchKeyword.value) {
    return options
  }
  return options.filter((option) => {
    if (option.excludeFromSearch) return false

    if (option.searchKey) {
      return option.searchKey.includes(searchKeyword.value.toLowerCase())
    } else {
      return option.label.includes(searchKeyword.value)
    }
  })
})

const selectItem = (option: SingleSelectBoxOption) => {
  emit('update:selectedOption', option)
  showOptionList.value = false
  isSearchMode.value = false
}

// 画面外クリックでドロップダウンを閉じる
const optionsRef = useTemplateRef<HTMLElement>('optionsRef')
onClickOutside(
  optionsRef,
  () => {
    showOptionList.value = false
    if (selectedOption) {
      isSearchMode.value = false

      searchKeyword.value = selectedOption.label
    }
  },
  { ignore: ['.text-input', '.remove-button'] }
)

const removeHandler = () => {
  emit('update:selectedOption', undefined)
  searchKeyword.value = ''
}

const inputTextHandler = (e: Event) => {
  const target = e.target as HTMLInputElement
  isSearchMode.value = true
  searchKeyword.value = target.value
}

const inputFocusHandler = () => {
  showOptionList.value = true
  // フォーカス時は検索モードを解除（全オプション表示）
  isSearchMode.value = false
}

onMounted(() => {
  if (selectedOption) {
    searchKeyword.value = selectedOption.label
  }
})

watch(
  () => selectedOption,
  (newVal) => {
    if (newVal) {
      searchKeyword.value = newVal.label
    } else {
      searchKeyword.value = ''
    }
  }
)
</script>

<template>
  <div class="single-select-box">
    <BaseCluster
      justify="space-between"
      align="center"
      class="single-select-form"
      :class="{ focused: showOptionList, disabled }"
    >
      <section class="left-content">
        <input
          v-if="!selectedOption"
          :value="searchKeyword"
          type="text"
          class="text-input"
          :placeholder="placeholder"
          @input="inputTextHandler"
          @focus="inputFocusHandler"
        />
        <BaseCluster v-else gap="xs" align="center" justify="space-between">
          <input
            type="text"
            :value="searchKeyword"
            class="text-input"
            @input="inputTextHandler"
            @focus="inputFocusHandler"
          />
          <BaseIcon v-if="clearable" :type="'close'" :size="20" class="remove-button" @click="removeHandler" />
        </BaseCluster>
      </section>

      <BaseCluster :align="'center'" class="stretch-icon" :class="{ disabled }">
        <BaseIcon v-show="showOptionList" :type="'collapse'" @click.stop="showOptionList = false" />
        <BaseIcon v-show="!showOptionList" :type="'expandMore'" @click.stop="showOptionList = true" />
      </BaseCluster>
    </BaseCluster>

    <div ref="optionsRef" class="options-wrapper">
      <ul v-if="!disabled && showOptionList" class="select-options">
        <template v-if="searchFilteredOptions.length === 0">
          <li class="options-item">
            <button disabled>
              <TextBody :size="'medium'">
                {{ $t('選択候補がありません。') }}
              </TextBody>
            </button>
          </li>
        </template>
        <template v-else>
          <li v-for="option in searchFilteredOptions" :key="option.value" class="options-item">
            <button @click="selectItem(option)">
              <TextBody :size="'medium'">
                {{ option.label }}
              </TextBody>
            </button>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.single-select-box {
  position: relative;
  width: v-bind(width);
}

.single-select-form {
  padding: 12px 12px 12px 16px;
  border: 1px solid var(--semantic-tokens-neutral-weak-border);
  border-radius: 4px;

  &.disabled {
    pointer-events: none;
    background-color: var(--semantic-tokens-neutral-xweak-surface);
  }

  &.focused {
    border-color: var(--semantic-tokens-brand-strong-border);
    box-shadow: 0px 0px 0px 3px var(--semantic-tokens-brand-weak-surface);
  }

  .left-content {
    width: 100%;
    padding-right: spacingToken.$spacing-s;

    .text-input {
      width: 100%;
      border: none;
      font-size: 16px;
      background-color: transparent;
      color: var(--semantic-tokens-neutral-default-text);
      @include textToken.body-large;

      &::placeholder {
        color: var(--semantic-tokens-neutral-xweak-text);
      }

      &:focus {
        outline: none;
      }

      &.disabled {
        cursor: not-allowed;
      }
    }

    .remove-button {
      cursor: pointer;
      margin-right: 8px;
    }
  }

  .stretch-icon {
    cursor: pointer;

    &.disabled {
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}

.options-wrapper {
  position: absolute;
  z-index: 1;
  width: 100%;
  top: calc(100% + spacingToken.$spacing-xs);

  .select-options {
    background-color: var(--semantic-tokens-neutral-default-background);
    padding: spacingToken.$spacing-xs 0;
    width: 100%;
    border: 1px solid var(--semantic-tokens-neutral-default-border);
    border-radius: 4px;
    box-shadow: 0px 1px 2px 0px rgba(57, 55, 55, 0.24), 0px 2px 6px 0px rgba(57, 55, 55, 0.14);
    max-height: v-bind(optionsHeight);
    overflow-y: auto;
    margin: 0;

    .options-item {
      box-sizing: border-box;
      display: flex;
      align-items: center;

      button {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        padding: 8px 16px;
        border: none;
        text-align: left;
        background-color: transparent;
        cursor: pointer;

        @include textToken.body-medium;

        &:not(:disabled):hover {
          background-color: var(--semantic-tokens-neutral-xweak-surface);
        }

        &:disabled {
          cursor: not-allowed;
          background-color: var(--semantic-tokens-neutral-xweak-surface);
        }
      }
    }
  }
}
</style>
